"use client";

import React from "react";
import NextLink, { type LinkProps } from "next/link";
import { useParams, usePathname } from "next/navigation";
import { localizePath, resolveCurrentLocale } from "@/utils/localize-path";

type LocalizedLinkProps = LinkProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps>;

const LocalizedLink = React.forwardRef<HTMLAnchorElement, LocalizedLinkProps>(
  function LocalizedLink({ href, ...props }, ref) {
    const params = useParams();
    const pathname = usePathname();
    const locale = resolveCurrentLocale(params?.lang, pathname);

    const localizedHref = React.useMemo(() => {
      if (typeof href === "string") {
        return localizePath(href, locale);
      }

      if (
        href &&
        typeof href === "object" &&
        "pathname" in href &&
        typeof href.pathname === "string"
      ) {
        return {
          ...href,
          pathname: localizePath(href.pathname, locale),
        };
      }

      return href;
    }, [href, locale]);

    return <NextLink ref={ref} href={localizedHref} {...props} />;
  },
);

export default LocalizedLink;
